import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { InfoSmallLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { DashSourceType } from "@mirage/analytics/events/enums/dash_source_type";
import { PAP_Click_NewSessionButton } from "@mirage/analytics/events/types/click_new_session_button";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import { PAP_Shown_StackChatRefreshSnackbar } from "@mirage/analytics/events/types/shown_stack_chat_refresh_snackbar";
import { ChatSourceStackItem } from "@mirage/mosaics/Chat/types";
import {
  normalizeResultForSummary,
  SummaryQnAEnum,
} from "@mirage/mosaics/SummaryQnaPanel/utils";
import {
  AskWithPromptMultiDocsParams,
  buildConversationRouterState,
  ConversationRouterState,
  CtaSource,
} from "@mirage/service-conversation/utils/buildConversationRouterState";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import {
  isSummarySupported,
  StackItemShortcutWithMetadata,
  Summarizable,
} from "@mirage/service-stacks/service/types";
import { hideSnackbar, showSnackbar } from "@mirage/shared/snackbar";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import { useCallback, useEffect, useMemo } from "react";
import { StackSourcesGroup } from "./StackSourcesGroup";
import { StackConversationSource } from "./types";

type StackSourcesAccordionProps = {
  chatSessionId?: string;
  documentIds: string[];
  enrichedStackItems: StackItemShortcutWithMetadata[];
  isLoading?: boolean;
  lastUserMessage: string;
  onRefreshConversation: (routerState?: ConversationRouterState) => void;
  serverConversationId?: string;
  sharingId: string;
};

type StackConversationSourceMap = {
  availableSources: StackConversationSource[];
  sources: StackConversationSource[];
  unavailableSources: StackConversationSource[];
};

/*
 * Note: This has a lot of similarities to both the existing CategorySearchAccordion and the
 * AnswerCitationCard components. At some point we should look to consolidate everything into
 * something more broadly composable, but in the interest of time I'm making the decision to
 * start with something net new.
 */
export function StackSourcesAccordion({
  chatSessionId,
  documentIds,
  enrichedStackItems,
  isLoading = false,
  lastUserMessage,
  onRefreshConversation,
  serverConversationId,
  sharingId,
}: StackSourcesAccordionProps) {
  const isDarkMode = useTheme();
  const { reportPapEvent } = useMirageAnalyticsContext();

  const onSourceClick = useCallback(
    // Available/newly available sources will have document uuids, but unavailable sources,
    // by definition, will not.
    (sourceType: DashSourceType, documentUuid?: string) => {
      reportPapEvent(
        PAP_Click_SourceFile({
          actionSurfaceComponent: "sidebar",
          chatSessionId,
          conversationId: serverConversationId,
          dashActionSurface: "conversation_page",
          dashSourceType: sourceType,
          featureLine: "stacks",
          resultUuid: documentUuid,
        }),
      );
    },
    [chatSessionId, reportPapEvent, serverConversationId],
  );

  const sourceMap = useMemo(() => {
    return enrichedStackItems.reduce(
      (acc, item) => {
        // Create a summarizable result for each Stack Item
        const normalizedItem = normalizeResultForSummary(
          item,
          SummaryQnAEnum.STACK_ITEM,
          isDarkMode,
        );

        // This shouldn't even happen for a Stack Item, but is required for type safety
        if (normalizedItem === null) {
          return acc;
        }

        // Map the summarizable result to a custom type that also includes a last modified timestamp for the source
        const stackConversationSource: StackConversationSource = {
          ...normalizedItem,
          lastModifiedMs: (item.last_modified_time_utc_sec || 0) * 1000,
        };

        // TODO: This cast is safe, in that isSummarySupported is just comparing normalizedItem.summarizableTag
        // to a string value, but it would be better to have the types line up so that this isn't necessary.
        if (
          stackConversationSource.uuid &&
          isSummarySupported(stackConversationSource as Summarizable)
        ) {
          // If an item is summarizable and is included in the conversation, it belongs in sources
          if (documentIds.includes(stackConversationSource.uuid)) {
            acc.sources.push(stackConversationSource);
          } else {
            // Otherwise, if an item is summarizable, but is not yet included in the conversation, it belongs in available sources
            acc.availableSources.push(stackConversationSource);
          }
        } else {
          // If an item is not summarizable, it belongs in unavailable sources
          acc.unavailableSources.push(stackConversationSource);
        }

        // Note that because we use the Stack Item as our source of truth, rather than the list of document ids,
        // any documentId without a matching Stack Item will be ignored. This is intentional, as we can't show any
        // information for a previously available source that no longer exists.
        return acc;
      },
      {
        sources: [],
        unavailableSources: [],
        availableSources: [],
      } as StackConversationSourceMap,
    );
  }, [enrichedStackItems, isDarkMode]);

  useEffect(() => {
    if (sourceMap.availableSources.length > 0) {
      reportPapEvent(
        PAP_Shown_StackChatRefreshSnackbar({
          actionSurfaceComponent: "stack_chat_snackbar",
          dashActionSurface: "conversation_page",
          featureLine: "stacks",
        }),
      );

      showSnackbar({
        buttons: [
          {
            label: i18n.t("stack_chat_start_new_chat"),
            onClick: () => {
              reportPapEvent(
                PAP_Click_NewSessionButton({
                  actionSurfaceComponent: "stack_chat_snackbar",
                  dashActionSurface: "conversation_page",
                  featureLine: "stacks",
                }),
              );

              const chatSources = enrichedStackItems.reduce((sources, item) => {
                if (isSummarySupported(item)) {
                  sources.push({
                    type: "stack_item",
                    stackItem: item,
                  });
                }

                return sources;
              }, [] as ChatSourceStackItem[]);

              const state = buildConversationRouterState(
                CtaSource.StackSuggestedPrompt,
                {
                  chatSources,
                  message: lastUserMessage,
                  scenarioMeta: {
                    type: "stack",
                    sharingId: sharingId,
                  },
                } satisfies AskWithPromptMultiDocsParams,
              );

              onRefreshConversation(state);
            },
          },
        ],
        timeoutMs: 0,
        title: `${i18n.t("stack_chat_new_sources_available", { count: sourceMap.availableSources.length })} ${i18n.t("stack_chat_start_new_chat_prompt")}`,
      });
    }

    return () => {
      // Hide the snackbar when the component unmounts or the available sources change
      hideSnackbar();
    };
  }, [sourceMap]);

  return (
    <Box marginY="24" width="100%">
      <StackSourcesGroup
        defaultOpen={true}
        hideTopBorder={true}
        isLoading={isLoading}
        onSourceClick={onSourceClick}
        sources={sourceMap.sources}
        sourceType="ordinary"
        title={i18n.t("stack_sources", { count: sourceMap.sources.length })}
      />
      {!isLoading && sourceMap.unavailableSources.length > 0 && (
        <StackSourcesGroup
          defaultOpen={false}
          onSourceClick={onSourceClick}
          sources={sourceMap.unavailableSources}
          sourceType="unavailable"
          title={
            <Box display="flex" alignItems="center">
              <DigTooltip
                isPortaled
                openDelay={400}
                placement="top"
                title={i18n.t("stack_unavailable_sources_disclaimer")}
              >
                <Box display="flex" alignItems="center" marginRight="8">
                  <UIIcon src={InfoSmallLine} size="medium" />
                </Box>
              </DigTooltip>
              {i18n.t("unavailable_stack_sources", {
                count: sourceMap.unavailableSources.length,
              })}
            </Box>
          }
        />
      )}
      {!isLoading && sourceMap.availableSources.length > 0 && (
        <StackSourcesGroup
          defaultOpen={false}
          onSourceClick={onSourceClick}
          sources={sourceMap.availableSources}
          sourceType="new_summarizable"
          title={
            <Box display="flex" alignItems="center">
              <DigTooltip
                isPortaled
                openDelay={400}
                placement="top"
                title={i18n.t("stack_newly_available_sources_tooltip")}
              >
                <Box display="flex" alignItems="center" marginRight="8">
                  <UIIcon src={InfoSmallLine} size="medium" />
                </Box>
              </DigTooltip>
              {i18n.t("newly_available_stack_sources", {
                count: sourceMap.availableSources.length,
              })}
            </Box>
          }
        />
      )}
    </Box>
  );
}
